import React, { useState, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Alert,
  Box,
  Button,
  Chip,
  Autocomplete
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
  CRITERIA_ID_FIELDS,
  CRITERIA_TAG_FIELDS
} from "../../../constants/spendingAggregations";
import { usePreviewSpendingAggregationVendors } from "../hooks/useSpendingAggregationActions";
import { useSubcategories } from "../../../hooks/useSubcategories";
import { useVendorAttributesList } from "../../../hooks/useVendorAttributesList";

const toIdArray = (value) => {
  if (value === undefined || value === null) {
    return [];
  }
  return (Array.isArray(value) ? value : [value]).map((id) => Number(id));
};

const isNonEmptyCriteria = (criteria) => {
  const hasIdField = CRITERIA_ID_FIELDS.some(
    ({ key }) => toIdArray(criteria[key]).length > 0
  );
  return (
    hasIdField ||
    criteria.minimumBrandRating !== undefined ||
    criteria.maximumBrandRating !== undefined
  );
};

const TypeaheadIdField = ({ title, placeholder, options, values, onChange }) => {
  const selected = values
    .map((id) => options.find((option) => option.id === id))
    .filter(Boolean);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selected}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => `${option.label} (id: ${option.id})`}
        onChange={(event, newValue) => onChange(newValue.map((o) => o.id))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={`${option.label} (id: ${option.id})`}
              deleteIcon={<DeleteIcon />}
              variant="outlined"
              color="primary"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} />
        )}
      />
    </Box>
  );
};

const SubcategoryTypeahead = ({ values, onChange }) => {
  const { data } = useSubcategories();
  const options = useMemo(
    () =>
      (data?.subcategories ?? []).map((subcategory) => ({
        id: subcategory.id,
        label: subcategory.joroSubcategory
      })),
    [data]
  );

  return (
    <TypeaheadIdField
      title="Joro Subcategory IDs"
      placeholder="Search subcategories by name"
      options={options}
      values={values}
      onChange={onChange}
    />
  );
};

const VendorAttributeTypeahead = ({ values, onChange }) => {
  const { data } = useVendorAttributesList();
  const options = useMemo(
    () =>
      (data?.vendorAttributes ?? []).map((attribute) => ({
        id: attribute.id,
        label: attribute.name
      })),
    [data]
  );

  return (
    <TypeaheadIdField
      title="Vendor Attribute IDs"
      placeholder="Search vendor attributes by name"
      options={options}
      values={values}
      onChange={onChange}
    />
  );
};

const TagIdField = ({ title, placeholder, values, onChange }) => {
  const [pending, setPending] = useState("");

  const add = () => {
    const trimmed = pending.trim();
    const parsed = parseInt(trimmed, 10);
    if (!trimmed || isNaN(parsed) || values.includes(parsed)) {
      return;
    }
    onChange([...values, parsed]);
    setPending("");
  };

  const remove = (index) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          label={`Add ${title}`}
          value={pending}
          onChange={(event) => setPending(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          size="small"
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={add}
          disabled={!pending.trim()}
          size="small"
        >
          Add
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {values.map((id, index) => (
          <Chip
            key={id}
            label={id}
            onDelete={() => remove(index)}
            deleteIcon={<DeleteIcon />}
            variant="outlined"
            color="primary"
          />
        ))}
        {values.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No {title.toLowerCase()} added yet
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const CriteriaBuilder = ({ criteria, onChange }) => {
  const { previewVendors } = usePreviewSpendingAggregationVendors();

  const updateField = (key, values) => {
    const next = { ...criteria };
    if (values.length > 0) {
      next[key] = values;
    } else {
      delete next[key];
    }
    onChange(next);
  };

  const updateRatingBound = (bound, value) => {
    const next = { ...criteria };
    if (value === "") {
      delete next[bound];
    } else {
      next[bound] = parseInt(value, 10);
    }
    onChange(next);
  };

  const handlePreview = () => {
    previewVendors.mutate({ criteria });
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">Selection Criteria</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        <Alert severity={isNonEmptyCriteria(criteria) ? "success" : "error"}>
          {isNonEmptyCriteria(criteria)
            ? "At least one criteria field is set — this aggregation resolves to a vendor set."
            : "At least one criteria field must be set."}
        </Alert>

        {CRITERIA_TAG_FIELDS.map(({ key, title, placeholder }) => (
          <TagIdField
            key={key}
            title={title}
            placeholder={placeholder}
            values={toIdArray(criteria[key])}
            onChange={(values) => updateField(key, values)}
          />
        ))}

        <VendorAttributeTypeahead
          values={toIdArray(criteria.vendorAttributeId)}
          onChange={(values) => updateField("vendorAttributeId", values)}
        />

        <SubcategoryTypeahead
          values={toIdArray(criteria.joroSubcategoryId)}
          onChange={(values) => updateField("joroSubcategoryId", values)}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Brand rating bounds
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Minimum (1–5)"
              type="number"
              inputProps={{ min: 1, max: 5 }}
              size="small"
              fullWidth
              value={criteria.minimumBrandRating ?? ""}
              onChange={(event) =>
                updateRatingBound("minimumBrandRating", event.target.value)
              }
            />
            <TextField
              label="Maximum (1–5)"
              type="number"
              inputProps={{ min: 1, max: 5 }}
              size="small"
              fullWidth
              value={criteria.maximumBrandRating ?? ""}
              onChange={(event) =>
                updateRatingBound("maximumBrandRating", event.target.value)
              }
            />
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handlePreview}
          disabled={previewVendors.isLoading}
          sx={{ alignSelf: "flex-start" }}
        >
          Preview matching vendors
        </Button>

        {previewVendors.isError && (
          <Alert severity="error">{previewVendors.error.message}</Alert>
        )}

        {previewVendors.isSuccess && (
          <Alert severity="info">
            {previewVendors.data.total} vendor
            {previewVendors.data.total === 1 ? "" : "s"} matched
            {previewVendors.data.vendors.length > 0 &&
              `: ${previewVendors.data.vendors
                .slice(0, 10)
                .map((vendor) => vendor.vendorName)
                .join(", ")}${previewVendors.data.total > 10 ? ", …" : ""}`}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
